import React, { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Cloud, Calendar, Zap } from 'lucide-react';
import { SharePointAuthButton } from '@/components/crm/SharePointAuthButton';
import { useSharePointAuth } from '@/hooks/useSharePointAuth';
import { OutlookConnectButton } from '@/components/crm/OutlookConnectButton';
import { useToast } from '@/hooks/use-toast';

export function IntegrationsTab() {
  const { toast } = useToast();
  const { isConnected: isSharePointConnected } = useSharePointAuth();

  // Handle OAuth errors
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');

    if (error) {
      toast({
        title: 'Koppeling mislukt',
        description: errorDescription || error,
        variant: 'destructive'
      });
      window.history.replaceState({}, '', window.location.pathname + '?tab=integrations');
    }
  }, [toast]);

  return (
    <div className="grid gap-4 sm:gap-6">
      {/* SharePoint Integration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Cloud className="h-5 w-5" />
              SharePoint Integratie
            </div>
            {isSharePointConnected ? (
              <span className="flex items-center gap-2 text-sm font-normal text-primary">
                <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                Verbonden
              </span>
            ) : (
              <span className="flex items-center gap-2 text-sm font-normal text-warning">
                <span className="h-2 w-2 rounded-full bg-warning" />
                Niet verbonden
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Koppel je SharePoint account om documenten te beheren en automatisch te synchroniseren met Microsoft 365.
          </p>
          
          <SharePointAuthButton />
          
          <div className="p-4 rounded-lg bg-muted">
            <h4 className="font-medium mb-2 text-sm">Mogelijkheden</h4>
            <div className="text-xs text-muted-foreground space-y-1">
              <p>• Automatische synchronisatie van project bestanden</p>
              <p>• Veilige opslag in Microsoft 365</p>
              <p>• Document versioning en history</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Outlook Calendar Integration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Outlook Agenda Integratie
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Bekijk en beheer je Outlook agenda direct in het CRM. Maak nieuwe afspraken aan en koppel ze aan projecten.
          </p>
          
          <OutlookConnectButton />
          
          <div className="p-4 rounded-lg bg-muted">
            <h4 className="font-medium mb-2 text-sm">Mogelijkheden</h4>
            <div className="text-xs text-muted-foreground space-y-1">
              <p>• Bekijk je Outlook agenda direct in het CRM</p>
              <p>• Maak nieuwe afspraken aan vanuit het CRM</p>
              <p>• Automatische synchronisatie met Microsoft 365</p>
              <p>• Koppel afspraken aan CRM-projecten (binnenkort)</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Coming Soon */}
      <Card className="border-dashed">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-muted-foreground">
            <Zap className="h-5 w-5" />
            Meer integraties binnenkort
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground space-y-2">
            <p>We werken aan meer integraties om je workflow te verbeteren:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Email (SMTP) voor geautomatiseerde meldingen</li>
              <li>ERP koppeling voor financiële data</li>
              <li>WhatsApp Business integratie</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
